import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useInbox } from '../contexts/InboxContext';
import { useBooking } from '../contexts/BookingContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger, DropdownMenuItem, DropdownMenuSeparator } from './ui/dropdown-menu';
import { SkillSOSLogo } from './SkillSOSLogo';
import { SimpleLanguageSelector as LanguageSelector } from './SimpleLanguageSelector';
import { CurrencySelector } from './CurrencySelector';
import { Wallet } from './Wallet';
import { 
  Menu, 
  User, 
  Settings, 
  Bell, 
  CreditCard, 
  HelpCircle, 
  LogOut,
  ArrowLeftRight,
  Star,
  Wrench,
  Calendar,
  DollarSign,
  Coins,
  Banknote,
  CheckCircle,
  MessageSquare,
  Shield,
  AlertCircle,
  XCircle,
  Clock
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const SharedHeader = ({ showBackButton = false, backText, backPath = "/" }) => {
  const { user, logout } = useAuth();
  const { unreadCount: inboxUnreadCount } = useInbox();
  const { myBookingsCount, bookingRequestsCount } = useBooking();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showWallet, setShowWallet] = useState(false);
  const [showInboxPanel, setShowInboxPanel] = useState(false);
  const [recentChanges, setRecentChanges] = useState([]);

  // Fetch recent changes for the dropdown
  useEffect(() => {
    const fetchRecentChanges = async () => {
      if (!user) return;
      
      try {
        const token = localStorage.getItem('skillsos_token');
        if (!token) return;
        
        const response = await axios.get(`${API}/bookings/recent-changes`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        setRecentChanges(response.data.changes || []);
      } catch (error) {
        console.error('Error fetching recent changes:', error);
      }
    };
    
    if (user) {
      fetchRecentChanges();
      // Refresh every 30 seconds
      const interval = setInterval(fetchRecentChanges, 30000);
      return () => clearInterval(interval);
    }
  }, [user]);

  // Debug logging for counts
  useEffect(() => {
    console.log('📬 Inbox unread count in SharedHeader:', inboxUnreadCount);
    console.log('📊 My Bookings count in SharedHeader:', myBookingsCount);
    console.log('📊 Booking Requests count in SharedHeader:', bookingRequestsCount);
  }, [inboxUnreadCount, myBookingsCount, bookingRequestsCount]);

  const handleSignIn = () => {
    navigate('/home', { state: { openAuth: true } });
  };

  const handleLogout = () => {
    logout();
    navigate('/home');
  };

  const displayBackText = backText || t('back');

  return (
    <header className="px-3 sm:px-6 py-2 flex justify-between items-center bg-white/90 backdrop-blur-sm border-b border-gray-200 shadow-sm overflow-x-hidden">
      {/* Left side - Logo and "How it works" */}
      <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
        <div className="sm:hidden">
          <SkillSOSLogo size="sm" variant="yellow-sos" showText={true} />
        </div>
        <div className="hidden sm:block">
          <SkillSOSLogo size="lg" variant="yellow-sos" />
        </div>
        {showBackButton && (
          <Button onClick={() => navigate(backPath)} variant="ghost" size="sm" className="text-gray-600 hover:text-gray-900">
            ← {displayBackText}
          </Button>
        )}
        {/* "How it works" link - next to logo for visibility */}
        {!user && !showBackButton && (
          <Button
            onClick={() => {
              const section = document.getElementById('how-it-works');
              if (section) {
                section.scrollIntoView({ behavior: 'smooth' });
              } else {
                navigate('/home#how-it-works');
              }
            }}
            variant="ghost"
            size="sm"
            className="text-purple-600 hover:text-purple-800 hover:bg-purple-50 font-semibold text-sm sm:text-base"
          >
            {t('howItWorks')}
          </Button>
        )}
      </div>

      {/* Right side - Controls */}
      <div className="flex items-center gap-1 sm:gap-3 flex-shrink-0">

        {/* Language Selector - Always visible */}
        <LanguageSelector />

        {user ? (
          // Authenticated user controls
          <>
            {/* Inbox Icon with Unread Badge */}
            <div className="relative">
              <Button
                onClick={() => navigate('/inbox')}
                variant="ghost"
                size="sm"
                className="relative"
                title={t('inbox')}
              >
                <MessageSquare className="w-5 h-5 text-gray-700" />
                {inboxUnreadCount > 0 && (
                  <Badge 
                    variant="destructive" 
                    className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"
                  >
                    {inboxUnreadCount}
                  </Badge>
                )}
              </Button>
            </div>

            {/* Notifications Dropdown - Consolidated */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  className="bg-blue-600 hover:bg-blue-700 text-white font-medium px-2 sm:px-4 py-2 rounded-lg flex items-center gap-1 sm:gap-2 relative"
                  title="Notifications"
                >
                  <Bell className="w-5 h-5 hidden sm:block" />
                  <span className="text-xs sm:text-sm">{t('notifications') || 'Notifications'}</span>
                  {(() => {
                    const totalNotifications = 
                      (myBookingsCount?.pending || 0) + 
                      (myBookingsCount?.statusChanged || 0) + 
                      (bookingRequestsCount?.pending || 0) + 
                      (bookingRequestsCount?.statusChanged || 0);
                    return totalNotifications > 0 ? (
                      <Badge className="ml-1 bg-red-500 text-white text-xs px-2 py-0.5">
                        {totalNotifications}
                      </Badge>
                    ) : null;
                  })()}
                </Button>
              </DropdownMenuTrigger>
              
              <DropdownMenuContent align="end" className="w-96 p-2 max-h-[600px] overflow-y-auto">
                <div className="p-2 border-b border-gray-200">
                  <h3 className="font-semibold text-gray-900 text-sm">{t('yourNotifications')}</h3>
                </div>
                
                {/* Recent Updates Section */}
                {recentChanges.length > 0 && (
                  <>
                    <div className="px-2 py-2 bg-gray-50 border-b border-gray-200">
                      <h4 className="font-medium text-gray-700 text-xs uppercase">{t('recentUpdates')}</h4>
                    </div>
                    {recentChanges.map((change, index) => (
                      <div
                        key={index}
                        onClick={() => {
                          // Navigate based on user's role in this booking
                          if (change.user_role === 'provider') {
                            // I'm the provider - go to booking requests (provider view)
                            navigate('/booking-requests');
                          } else {
                            // I'm the client - go to my bookings (client view)
                            navigate('/my-bookings');
                          }
                        }}
                        className={`px-4 py-3 border-b border-gray-100 cursor-pointer hover:opacity-80 transition-opacity ${
                          change.type === 'cancelled' ? 'bg-red-50 hover:bg-red-100' : 'bg-green-50 hover:bg-green-100'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                            change.type === 'cancelled' ? 'bg-red-100' : 'bg-green-100'
                          }`}>
                            {change.type === 'cancelled' ? (
                              <XCircle className="w-4 h-4 text-red-600" />
                            ) : (
                              <CheckCircle className="w-4 h-4 text-green-600" />
                            )}
                          </div>
                          <div className="flex-1">
                            <p className={`font-medium text-sm ${
                              change.type === 'cancelled' ? 'text-red-900' : 'text-green-900'
                            }`}>
                              {change.message}
                            </p>
                            <p className={`text-xs mt-1 ${
                              change.type === 'cancelled' ? 'text-red-700' : 'text-green-700'
                            }`}>
                              {change.reason === 'Other' ? t('other') :
                               change.reason === 'Schedule conflict' ? t('scheduleConflict') :
                               change.reason === 'Found another provider' ? t('foundAnotherProvider') :
                               change.reason === 'Emergency' ? t('emergency') :
                               change.reason === 'Service no longer needed' ? t('serviceNoLongerNeeded') :
                               change.reason === 'Cannot provide service' ? t('cannotProvideService') :
                               change.reason === 'Client request' ? t('clientRequest') :
                               change.reason || ''}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                    <DropdownMenuSeparator />
                  </>
                )}
                
                {/* My Bookings Option */}
                <DropdownMenuItem 
                  onClick={() => navigate('/my-bookings')}
                  className="flex items-center justify-between gap-3 px-4 py-3 cursor-pointer hover:bg-blue-50 rounded-lg my-1"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <Calendar className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 text-sm">{t('myBookings')}</p>
                      <p className="text-xs text-gray-500">{t('viewYourBookingRequests')}</p>
                    </div>
                  </div>
                  {((myBookingsCount?.pending || 0) > 0 || (myBookingsCount?.statusChanged || 0) > 0) && (
                    <Badge variant="destructive" className="text-xs">
                      {(myBookingsCount?.pending || 0) + (myBookingsCount?.statusChanged || 0)}
                    </Badge>
                  )}
                </DropdownMenuItem>

                {/* Admin Verifications Option - Only for Admins */}
                {user?.email === 'sne.nikolova@gmail.com' && (
                  <DropdownMenuItem 
                    onClick={() => navigate('/admin/verifications')}
                    className="flex items-center justify-between gap-3 px-4 py-3 cursor-pointer hover:bg-purple-50 rounded-lg my-1"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                        <Shield className="w-5 h-5 text-purple-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900 text-sm">{t('adminVerifications')}</p>
                        <p className="text-xs text-gray-500">{t('reviewPendingVerifications')}</p>
                      </div>
                    </div>
                  </DropdownMenuItem>
                )}

                {/* Booking Requests Option */}
                <DropdownMenuItem 
                  onClick={() => navigate('/booking-requests')}
                  className="flex items-center justify-between gap-3 px-4 py-3 cursor-pointer hover:bg-green-50 rounded-lg my-1"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 text-sm">{t('bookingRequests')}</p>
                      <p className="text-xs text-gray-500">{t('manageIncomingRequests')}</p>
                    </div>
                  </div>
                  {(bookingRequestsCount?.pending || 0) > 0 && (
                    <Badge variant="destructive" className="text-xs">
                      {bookingRequestsCount?.pending || 0}
                    </Badge>
                  )}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Main User Dropdown Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="flex items-center gap-2 hover:bg-gray-100 rounded-full px-3 py-2 border border-gray-300">
                  <Menu className="w-4 h-4 text-gray-600" />
                  <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white text-sm font-medium">
                    {user.name?.charAt(0).toUpperCase()}
                  </div>
                </Button>
              </DropdownMenuTrigger>
              
              <DropdownMenuContent align="end" className="w-80 p-0 shadow-xl border border-gray-200">
                {/* User Info Header */}
                <div className="p-4 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-purple-50">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white text-lg font-medium">
                      {user.name?.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900">{user.name}</h3>
                      <p className="text-sm text-gray-600">{user.email}</p>
                    </div>
                  </div>
                </div>

                {/* Currency Selector - Removed: Bulgaria adopted EUR */}
                {/* 
                <div className="p-3 border-b border-gray-100">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <CreditCard className="w-4 h-4 text-gray-500" />
                      <span className="text-sm font-medium text-gray-700">{t('currency')}</span>
                    </div>
                    <CurrencySelector />
                  </div>
                </div>
                */}

                {/* Menu Items */}
                <div className="py-2">
                  <DropdownMenuItem 
                    onClick={() => navigate('/my-profile')}
                    className="flex items-center gap-3 px-4 py-2 text-sm cursor-pointer hover:bg-blue-50"
                  >
                    <User className="w-4 h-4 text-blue-600" />
                    {t('myProfile')}
                  </DropdownMenuItem>

                  <DropdownMenuItem 
                    onClick={() => navigate('/my-bookings')}
                    className="flex items-center justify-between gap-3 px-4 py-2 text-sm cursor-pointer hover:bg-purple-50"
                  >
                    <div className="flex items-center gap-3">
                      <Calendar className="w-4 h-4 text-purple-600" />
                      {t('myBookings')}
                    </div>
                    {((myBookingsCount?.pending || 0) > 0 || (myBookingsCount?.statusChanged || 0) > 0) && (
                      <Badge variant="destructive" className="text-xs">
                        {(myBookingsCount?.pending || 0) + (myBookingsCount?.statusChanged || 0)}
                      </Badge>
                    )}
                  </DropdownMenuItem>

                  <DropdownMenuItem 
                    onClick={() => navigate('/booking-requests')}
                    className="flex items-center justify-between gap-3 px-4 py-2 text-sm cursor-pointer hover:bg-green-50"
                  >
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      {t('bookingRequests')}
                    </div>
                    {(bookingRequestsCount?.pending || 0) > 0 && (
                      <Badge variant="destructive" className="text-xs">
                        {bookingRequestsCount?.pending || 0}
                      </Badge>
                    )}
                  </DropdownMenuItem>

                  <DropdownMenuItem 
                    onClick={() => navigate('/inbox')}
                    className="flex items-center justify-between gap-3 px-4 py-2 text-sm cursor-pointer hover:bg-orange-50"
                  >
                    <div className="flex items-center gap-3">
                      <MessageSquare className="w-4 h-4 text-orange-600" />
                      {t('inbox')}
                    </div>
                    {inboxUnreadCount > 0 && (
                      <Badge variant="destructive" className="text-xs">
                        {inboxUnreadCount}
                      </Badge>
                    )}
                  </DropdownMenuItem>

                  {/* Wallet - Hidden for cleaner UX, feature still available */}
                  {/* 
                  <DropdownMenuItem 
                    onClick={() => setShowWallet(true)}
                    className="flex items-center gap-3 px-4 py-2 text-sm cursor-pointer hover:bg-green-50"
                  >
                    <DollarSign className="w-4 h-4 text-green-600" />
                    {t('wallet')}
                  </DropdownMenuItem>
                  */}

                  <DropdownMenuSeparator />
                  
                  <DropdownMenuItem 
                    onClick={handleLogout}
                    className="flex items-center gap-3 px-4 py-2 text-sm cursor-pointer text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <LogOut className="w-4 h-4" />
                    {t('logout')}
                  </DropdownMenuItem>
                </div>
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        ) : (
          // Unauthenticated user controls
          <>
            <Button 
              onClick={handleSignIn} 
              variant="ghost" 
              size="sm" 
              className="text-gray-700 hover:text-gray-900 text-xs sm:text-sm px-2 sm:px-3"
              data-testid="login-btn"
            >
              {t('signIn')}
            </Button>
            <Button 
              onClick={handleSignIn} 
              size="sm" 
              className="bg-blue-600 hover:bg-blue-700 text-white text-xs sm:text-sm px-2 sm:px-4 relative overflow-hidden"
              data-testid="get-started-btn"
            >
              <span className="absolute -top-1 -right-1 bg-yellow-400 text-gray-900 text-[8px] font-bold px-1 rounded-sm transform rotate-12">{t('free')}</span>
              {t('getStarted')}
            </Button>
          </>
        )}
      </div>
      
      {/* Wallet Modal */}
      <Wallet open={showWallet} onOpenChange={setShowWallet} />
    </header>
  );
};